import type { SubjectProgress } from "./type.app.js";

export type MathOperation = "addition" | "subtraction" | "multiplication" | "division" | "skip";

export type MathQuestion = {
  id: string;
  level: number;
  operation: MathOperation;
  left: number;
  right: number;
  answer: number;
  text: string;
  hint: string | null;
};

type Pair = { left: number; right: number };

export const SKIP_COUNTING_ORDER = [10, 2, 5, 9, 8, 3, 4, 6, 7] as const;

const range = (min: number, max: number): number[] =>
  Array.from({ length: Math.max(0, max - min + 1) }, (_, index) => min + index);

const pairs = (
  left: number[],
  right: number[],
  predicate: (left: number, right: number) => boolean = () => true,
): Pair[] =>
  left.flatMap((leftValue) =>
    right
      .filter((rightValue) => predicate(leftValue, rightValue))
      .map((rightValue) => ({ left: leftValue, right: rightValue })),
  );

const levelOperation = (level: number): MathOperation => {
  if ([2, 11, 31, 41, 51].includes(level)) return "skip";
  if (level <= 25) return level === 1 || level === 6 || level >= 12 ? "addition" : "subtraction";
  if (level <= 50 || (level >= 61 && level <= 70) || (level >= 91 && level <= 95)) return "multiplication";
  return "division";
};

export const operationForLevel = (level: number): MathOperation => levelOperation(level);

const finitePairsForLevel = (level: number): Pair[] => {
  if (level === 1) return pairs(range(0, 5), range(0, 5));
  if (level === 6) return pairs(range(6, 10), range(6, 10));
  if (level >= 3 && level <= 5) return pairs(range(0, 10), range(0, 10), (left, right) => left >= right);
  if (level >= 7 && level <= 10) return pairs(range(11, 20), range(0, 10), (left, right) => left >= right);
  if (level >= 12 && level <= 15) return pairs(range(11, 100), range(0, 10));
  if (level >= 16 && level <= 20) return pairs(range(11, 100), range(0, 10), (left, right) => left >= right);
  if (level >= 21 && level <= 25) return pairs(range(11, 100), range(11, 20));
  if (level === 2)
    return [
      { left: 2, right: 100 },
      { left: 5, right: 100 },
      { left: 10, right: 100 },
    ];
  if (level === 11)
    return [
      { left: 3, right: 100 },
      { left: 4, right: 100 },
    ];
  if (level === 31)
    return [
      { left: 6, right: 100 },
      { left: 7, right: 100 },
    ];
  if (level === 41)
    return [
      { left: 8, right: 100 },
      { left: 9, right: 100 },
    ];
  if (level === 51)
    return [
      { left: 11, right: 100 },
      { left: 12, right: 100 },
    ];
  if (level >= 26 && level <= 30) return pairs([0, 1, 2, 3, 4, 5, 10], [0, 1, 2, 3, 4, 5, 10]);
  if (level >= 32 && level <= 40) return pairs([0, 1, 2, 3, 4, 5, 6, 7, 10], [0, 1, 2, 3, 4, 5, 6, 7, 10]);
  if (level >= 42 && level <= 50) return pairs(range(0, 10), range(0, 10));
  if (level >= 52 && level <= 60) return pairs(range(0, 12), [1, 2, 5, 10]);
  if ((level >= 61 && level <= 70) || (level >= 91 && level <= 95)) return pairs(range(0, 12), range(0, 12));
  if (level >= 71 && level <= 80) return pairs(range(0, 12), [1, 2, 3, 4, 5, 10]);
  return pairs(range(0, 12), range(1, 11));
};

const isGeneratedAddition = (level: number): boolean => level >= 12 && level <= 25;

export const mathPairsForLevel = (level: number): Pair[] => finitePairsForLevel(level);

const answerFor = (operation: MathOperation, left: number, right: number): number => {
  if (operation === "addition") return left + right;
  if (operation === "subtraction") return left - right;
  if (operation === "multiplication") return left * right;
  if (operation === "division") return left / right;
  return left * right;
};

const operationSymbol = (operation: MathOperation): string =>
  operation === "addition"
    ? "+"
    : operation === "subtraction"
      ? "-"
      : operation === "multiplication"
        ? "×"
        : operation === "division"
          ? "÷"
          : "skip count by";

const hintFactor = (question: MathQuestion): number => {
  if (question.operation === "skip") return question.left;
  if (question.left === 0) return question.right;
  if (question.right === 0) return question.left;
  const leftRank = SKIP_COUNTING_ORDER.indexOf(question.left as (typeof SKIP_COUNTING_ORDER)[number]);
  const rightRank = SKIP_COUNTING_ORDER.indexOf(question.right as (typeof SKIP_COUNTING_ORDER)[number]);
  return leftRank <= rightRank ? question.left : question.right;
};

const hintText = (question: MathQuestion, hidden: number): string | null => {
  if (question.operation !== "multiplication" && question.operation !== "skip") return null;
  const factor = hintFactor(question);
  const count = question.operation === "skip" ? Math.floor(question.right / factor) : question.answer / factor;
  const values = count === 0 ? [0] : range(1, count + 1).map((step) => step * factor);
  if (hidden >= values.length) return null;
  const visible = Math.max(0, values.length - hidden);
  const sequence = values.map((value, index) => (index < visible ? value : "?")).join(", ");
  const special =
    question.operation === "multiplication" && question.left === 8 && question.right === 8
      ? "I ate and I ate and I got sick on the floor"
      : question.operation === "multiplication" &&
          ((question.left === 6 && question.right === 6) || (question.left === 6 && question.right === 8))
        ? `Six times ${question.right} is ${question.answer} (sing it with a tune)`
        : question.operation === "multiplication" && (question.left === 10 || question.right === 10)
          ? "When multiplying anything by 10 just add a zero"
          : (question.operation === "multiplication" || question.operation === "skip") && factor === 9
            ? "Count the ones digit backwards as you count the tens digit forwards"
            : (question.operation === "multiplication" || question.operation === "skip") && factor === 8
              ? "Count the ones digit backwards by 2 as you count the tens digit forwards"
              : null;
  return special ? `${special}: ${sequence}` : sequence;
};

export const questionWithHint = (question: MathQuestion, progress: SubjectProgress): MathQuestion => ({
  ...question,
  hint: hintText(question, progress.mathHintHidden[question.id] ?? 0),
});

export const questionFromPair = (level: number, pair: Pair): MathQuestion => {
  const operation = operationForLevel(level);
  const answer = answerFor(operation, pair.left, pair.right);
  const text =
    operation === "skip"
      ? `Skip count by ${pair.left} to ${pair.right}`
      : `${pair.left} ${operationSymbol(operation)} ${pair.right}`;
  return {
    id: `${level}-${operation}-${pair.left}-${pair.right}`,
    level,
    operation,
    left: pair.left,
    right: pair.right,
    answer,
    text,
    hint: null,
  };
};

export const questionForLevel = (
  level: number,
  progress?: SubjectProgress,
  random = Math.random,
  excludeId?: string,
): MathQuestion => {
  const pairsForLevel = mathPairsForLevel(level);
  let pair: Pair;
  if (isGeneratedAddition(level)) {
    const roll = (): Pair => ({ left: 11 + Math.floor(random() * 90), right: Math.floor(random() * 11) });
    pair = roll();
    for (let attempt = 0; attempt < 8 && questionFromPair(level, pair).id === excludeId; attempt += 1) pair = roll();
  } else {
    const allowed = pairsForLevel.filter((candidate) => questionFromPair(level, candidate).id !== excludeId);
    const unseen = allowed.filter((candidate) => {
      const question = questionFromPair(level, candidate);
      return !(progress?.mathSeen[question.id] ?? 0);
    });
    const candidates = unseen.length ? unseen : allowed.length ? allowed : pairsForLevel;
    pair = candidates[Math.floor(random() * Math.max(1, candidates.length))] ?? { left: 0, right: 0 };
  }
  const question = questionFromPair(level, pair);
  return { ...question, hint: hintText(question, progress?.mathHintHidden[question.id] ?? 0) };
};

export const levelCoverageComplete = (level: number, progress: SubjectProgress): boolean => {
  if (isGeneratedAddition(level))
    return Object.keys(progress.mathSeen).filter((id) => id.startsWith(`${level}-addition-`)).length >= 50;
  return mathPairsForLevel(level).every((pair) => {
    const question = questionForPairWithHint(level, pair, progress);
    if (!progress.mathSeen[question.id]) return false;
    return question.operation !== "multiplication" || progress.mathHintHidden[question.id] >= hintLength(question);
  });
};

const questionForPairWithHint = (level: number, pair: Pair, progress: SubjectProgress): MathQuestion => {
  const question = questionFromPair(level, pair);
  return questionWithHint(question, progress);
};

export const hintLength = (question: MathQuestion): number => {
  const factor = hintFactor(question);
  const count = question.operation === "skip" ? Math.floor(question.right / factor) : question.answer / factor;
  return count === 0 ? 1 : count;
};

export const mathLevelReady = (level: number, progress: SubjectProgress): boolean => {
  const results = progress.events.map((event) => event.result === "right" || event.result === "wayTooEasy");
  const recent = results.slice(-20);
  return (
    levelCoverageComplete(level, progress) &&
    recent.length >= 20 &&
    recent.filter(Boolean).length >= 17 &&
    results.slice(-5).length === 5 &&
    results.slice(-5).every(Boolean)
  );
};

export const updateMathHint = (
  question: MathQuestion,
  progress: SubjectProgress,
  result: "right" | "wrong",
): Record<string, number> => {
  const current = progress.mathHintHidden[question.id] ?? 0;
  const count = hintLength(question);
  if (!count) return progress.mathHintHidden;
  const next =
    result === "right" ? Math.min(count, current + 2) : current >= count ? count - 1 : Math.max(0, current - 1);
  return { ...progress.mathHintHidden, [question.id]: next };
};
